import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Table,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Input, InputGroup, InputGroupText, Spinner, Button, Badge
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import { URLS } from "../../Url";
import axios from "axios";
import { Link } from "react-router-dom";

// Inline CSS styles
const styles = `
.dashboard-container {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.hover-lift {
  transition: all 0.3s ease;
}

.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.report-card {
  border: none;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.badge-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  border: none;
}

.badge-soft-success {
  background-color: rgba(52, 195, 143, 0.1);
  color: #34c38f;
  border: none;
}

.badge-soft-info {
  background-color: rgba(80, 165, 241, 0.1);
  color: #50a5f1;
  border: none;
}

.badge-soft-warning {
  background-color: rgba(241, 180, 76, 0.1);
  color: #f1b44c;
  border: none;
}

.badge-soft-danger {
  background-color: rgba(244, 106, 106, 0.1);
  color: #f46a6a;
  border: none;
}

.badge-soft {
  border: none;
  font-weight: 500;
}

/* Custom scrollbar for tables */
.table-responsive-custom {
  max-height: 400px;
}

.table-responsive-custom::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-responsive-custom::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.table-responsive-custom::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.table-responsive-custom::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Card hover effects */
.card-hover {
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.card-hover:hover {
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  border-color: #d0d0d0;
}

/* Loading animation */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.loading-pulse {
  animation: pulse 2s infinite;
}

/* Status badges */
.status-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Custom button styles */
.btn-custom {
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

/* Table header styles */
.table-header-custom {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.table-header-custom th {
  border: none;
  padding: 12px 16px;
  font-weight: 600;
}

/* Gradient backgrounds for cards */
.gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.gradient-success {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.gradient-warning {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.gradient-info {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.gradient-danger {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

/* Custom shadow */
.custom-shadow {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Hover effects for table rows */
.table-hover-custom tbody tr {
  transition: all 0.3s ease;
}

.table-hover-custom tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
  transform: translateX(2px);
}

/* Icon styles */
.icon-container {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
}

/* Filter dropdown styles */
.filter-dropdown .dropdown-toggle {
  border-radius: 8px;
  font-weight: 500;
}

/* Responsive text */
.responsive-text {
  font-size: clamp(0.8rem, 2vw, 1rem);
}

/* Chart container */
.chart-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
}

/* Pagination styles */
.pagination-custom .page-item.active .page-link {
  background-color: #556ee6;
  border-color: #556ee6;
}

.pagination-custom .page-link {
  color: #556ee6;
  border-radius: 6px;
  margin: 0 2px;
}

/* Search input styles */
.search-input {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.2rem rgba(85, 110, 230, 0.25);
}

/* Stats highlight */
.stats-highlight {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Cake chart specific styles */
.cake-chart-card {
  border: 1px solid #e9ecef;
}

.cake-chart-header {
  padding: 0 10px;
}

.cake-filter-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}
`;

// Inject styles into the document head
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const Dashboard = props => {
  const [dashboardData, setDashboardData] = useState({
    theatres: 0,
    occasions: 0,
    bookings: 0,
    amount: 0,
    posAmount: 0,
    latestBookings: [],
    staff: 0,
    departments: 0,
    services: 0
  });
  
  const [loading, setLoading] = useState({
    main: true,
    profit: false,
    staff: false,
    cakes: false,
    theaters: false
  });
  
  const [filterBooking, setFilterBooking] = useState("This Week");
  const [filterCake, setFilterCake] = useState("This Month");
  const [dropdownOpenBooking, setDropdownOpenBooking] = useState(false);
  const [dropdownOpenCake, setDropdownOpenCake] = useState(false);
  
  // Cake filter states
  const [selectedCake, setSelectedCake] = useState("");
  const [selectedCakeMonth, setSelectedCakeMonth] = useState("");
  
  const [cakeRevenueData, setCakeRevenueData] = useState({
    months: [],
    revenues: [],
    cakeNames: []
  });
  
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [theaters, setTheaters] = useState([]);
  const [filteredTheaters, setFilteredTheaters] = useState([]);
  const [cakes, setCakes] = useState([]);
  const [filteredCakes, setFilteredCakes] = useState([]);
  const [staffRevenue, setStaffRevenue] = useState([]);
  const [filteredStaffRevenue, setFilteredStaffRevenue] = useState([]);
  const [staffFilter, setStaffFilter] = useState("This Month");
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpenStaff, setDropdownOpenStaff] = useState(false);
  const [profitData, setProfitData] = useState([]);
  const [filteredProfitData, setFilteredProfitData] = useState([]);
  
  // Profit report filters
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMonthProfit, setSelectedMonthProfit] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [profitSearchTerm, setProfitSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Latest Bookings filter
  const [selectedBookingSource, setSelectedBookingSource] = useState("");
  const [filteredLatestBookings, setFilteredLatestBookings] = useState([]);

  // Current filter data - BACKEND KE RESPONSE KO DIRECT RAKHO
  const [currentFilterData, setCurrentFilterData] = useState({
    bookings: 0,
    amount: 0,
    details: []
  });

  const token = JSON.parse(localStorage.getItem("authUser"))?.token;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format number with commas
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0);
  };

  // Fetch main dashboard data - SIMPLE FIX
  // Fetch main dashboard data - FIXED VERSION
const fetchDashboardData = async (filter) => {
  try {
    console.log("Fetching dashboard data with filter:", filter);
    
    const response = await axios.post(
      "https://api.carnivalcastle.com/v1/carnivalApi/admin/dashbaord",
      { type: filter },
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      }
    );
    
    if (response.data.success) {
      console.log("Dashboard data received successfully:", response.data);
      
      // Set dashboard data
      setDashboardData(response.data);
      
      // Initialize filtered bookings with all latest bookings
      setFilteredLatestBookings(response.data.latestBookings || []);
      
      // IMPORTANT: Check for case-insensitive keys in response
      const filterKeys = {
        'today': 'today',
        'this week': 'thisWeek',
        'last week': 'lastWeek',
        'this month': 'thisMonth',
        'last month': 'lastMonth'
      };
      
      // Get lowercase filter key
      const lowercaseFilter = filter.toLowerCase();
      const responseKey = filterKeys[lowercaseFilter];
      
      console.log("Looking for key in response:", responseKey);
      console.log("Available keys:", Object.keys(response.data));
      
      // Get filter data from response
      if (responseKey && response.data[responseKey]) {
        console.log(`Found data for ${responseKey}:`, response.data[responseKey]);
        setCurrentFilterData(response.data[responseKey]);
      } else {
        console.log(`No data found for ${responseKey}, checking direct match...`);
        
        // Try direct match
        const directKey = lowercaseFilter.replace(/\s+/g, '');
        if (response.data[directKey]) {
          console.log(`Found data with direct key ${directKey}:`, response.data[directKey]);
          setCurrentFilterData(response.data[directKey]);
        } else {
          console.log(`No data found, setting default`);
          setCurrentFilterData({
            bookings: 0,
            amount: 0,
            details: []
          });
        }
      }
    } else {
      console.error("API returned success: false", response.data);
    }
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
  }
};
  useEffect(() => {
    fetchDashboardData(filterBooking);
  }, [filterBooking]);

  // Fetch all initial data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(prev => ({ ...prev, main: true }));
        
        await Promise.all([
          fetchDashboardData(filterBooking),
          fetchTheaters(),
          fetchCakes(),
          fetchStaffRevenue(staffFilter),
          fetchProfitData()
        ]);
        
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(prev => ({ ...prev, main: false }));
      }
    };

    fetchAllData();
  }, []);

  // Filter latest bookings when source changes or dashboard data updates
  useEffect(() => {
    if (dashboardData.latestBookings) {
      let filtered = dashboardData.latestBookings;
      
      if (selectedBookingSource) {
        filtered = filtered.filter(booking => 
          booking.bookingSource && 
          booking.bookingSource.toLowerCase().includes(selectedBookingSource.toLowerCase())
        );
      }
      
      setFilteredLatestBookings(filtered);
    }
  }, [selectedBookingSource, dashboardData.latestBookings]);

  // Filter theaters based on selected month and address
  useEffect(() => {
    let filtered = theaters;

    if (selectedMonth) {
      filtered = filtered.filter(theater => 
        theater.monthName && 
        theater.monthName.toLowerCase() === selectedMonth.toLowerCase()
      );
    }

    if (selectedAddress) {
      filtered = filtered.filter(theater => {
        const addressName = theater.address?.name || theater.theatre?.address?.name;
        return addressName && addressName.toLowerCase().includes(selectedAddress.toLowerCase());
      });
    }

    setFilteredTheaters(filtered);
  }, [selectedMonth, selectedAddress, theaters]);

  const fetchTheaters = async () => {
    try {
      setLoading(prev => ({ ...prev, theaters: true }));
      const response = await axios.post(
        "https://api.carnivalcastle.com/v1/carnivalApi/admin/booking/getmostbookedtheatre", 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setTheaters(response.data.data);
        setFilteredTheaters(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching theaters:", error);
    } finally {
      setLoading(prev => ({ ...prev, theaters: false }));
    }
  };

  const fetchCakes = async () => {
    try {
      setLoading(prev => ({ ...prev, cakes: true }));
      
      const response = await axios.post(
        `https://api.carnivalcastle.com/v1/carnivalApi/admin/booking/getmostdemandedcakes`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        setCakes(data);
        setFilteredCakes(data);
        
        processCakeChartData(data);
      }
    } catch (error) {
      console.error("Error fetching cakes:", error);
    } finally {
      setLoading(prev => ({ ...prev, cakes: false }));
    }
  };

  // Process cake data for chart
  const processCakeChartData = (cakeData) => {
    if (!cakeData || cakeData.length === 0) {
      setCakeRevenueData({
        months: [],
        revenues: [],
        cakeNames: []
      });
      return;
    }

    const chartData = {};
    
    cakeData.forEach(item => {
      const key = `${item.monthName} ${item.year}`;
      if (!chartData[key]) {
        chartData[key] = {
          month: key,
          revenue: 0,
          cakes: []
        };
      }
      chartData[key].revenue += item.totalRevenue;
      chartData[key].cakes.push({
        name: item.cakeName,
        revenue: item.totalRevenue
      });
    });

    const months = Object.keys(chartData);
    const revenues = Object.values(chartData).map(item => item.revenue);
    
    setCakeRevenueData({
      months,
      revenues,
      cakeNames: months.map(month => chartData[month].cakes[0]?.name || 'Multiple Cakes')
    });
  };

  // Filter cakes based on selected cake and month
  const filterCakesData = () => {
    let filtered = cakes;

    if (selectedCake) {
      filtered = filtered.filter(cake => cake.cakeName === selectedCake);
    }

    if (selectedCakeMonth) {
      filtered = filtered.filter(cake => `${cake.monthName} ${cake.year}` === selectedCakeMonth);
    }

    setFilteredCakes(filtered);
    processCakeChartData(filtered);
  };

  // Get unique cakes and months for filters
  const getUniqueCakes = () => {
    const uniqueCakes = [...new Set(cakes.map(cake => cake.cakeName))];
    return uniqueCakes.filter(Boolean);
  };

  const getUniqueMonths = () => {
    const uniqueMonths = [...new Set(cakes.map(cake => `${cake.monthName} ${cake.year}`))];
    return uniqueMonths.filter(Boolean);
  };

  // Get unique months and addresses for theater filters
  const getUniqueTheaterMonths = () => {
    const uniqueMonths = [...new Set(theaters.map(theater => theater.monthName))];
    return uniqueMonths.filter(Boolean);
  };

  const getUniqueAddresses = () => {
    const uniqueAddresses = [...new Set(theaters
      .map(theater => {
        return theater.address?.name || theater.theatre?.address?.name;
      })
      .filter(Boolean)
    )];
    return uniqueAddresses;
  };

  // Get unique booking sources for latest bookings filter
  const getUniqueBookingSources = () => {
    if (!dashboardData.latestBookings) return [];
    const uniqueSources = [...new Set(dashboardData.latestBookings.map(booking => booking.bookingSource))];
    return uniqueSources.filter(Boolean);
  };

  // Apply cake filters
  const applyCakeFilters = () => {
    filterCakesData();
  };

  // Reset cake filters
  const resetCakeFilters = () => {
    setSelectedCake("");
    setSelectedCakeMonth("");
    setFilteredCakes(cakes);
    processCakeChartData(cakes);
  };

  // Reset theater filters
  const resetTheaterFilters = () => {
    setSelectedMonth("");
    setSelectedAddress("");
    setFilteredTheaters(theaters);
  };

  // Reset latest bookings filter
  const resetLatestBookingsFilter = () => {
    setSelectedBookingSource("");
    setFilteredLatestBookings(dashboardData.latestBookings || []);
  };

  const fetchStaffRevenue = async (filter) => {
    try {
      setLoading(prev => ({ ...prev, staff: true }));
      const response = await axios.post(
        "https://api.carnivalcastle.com/v1/carnivalApi/admin/booking/staffrevenue",
        { filter },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStaffRevenue(response.data.data);
        setFilteredStaffRevenue(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching staff revenue:", error);
    } finally {
      setLoading(prev => ({ ...prev, staff: false }));
    }
  };

  const fetchProfitData = async (category = "", month = "", year = "") => {
    try {
      setLoading(prev => ({ ...prev, profit: true }));
      const params = new URLSearchParams();
      if (category) params.append("category", category);
      if (month) params.append("month", month);
      if (year) params.append("year", year);

      const response = await axios.post(
        `https://api.carnivalcastle.com/v1/carnivalApi/admin/booking/getprofitbycat?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setProfitData(response.data.data);
        setFilteredProfitData(response.data.data);
      } else {
        setProfitData([]);
        setFilteredProfitData([]);
      }
    } catch (error) {
      console.error("Error fetching profit data:", error);
      setProfitData([]);
      setFilteredProfitData([]);
    } finally {
      setLoading(prev => ({ ...prev, profit: false }));
    }
  };

  // Filter profit data based on search
  useEffect(() => {
    const filtered = profitData.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(profitSearchTerm.toLowerCase())
    );
    setFilteredProfitData(filtered);
    setCurrentPage(1);
  }, [profitSearchTerm, profitData]);

  // Filter staff revenue based on search
  useEffect(() => {
    const filtered = staffRevenue.filter(staff =>
      staff.staffName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.staffEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.staffPhone?.includes(searchTerm)
    );
    setFilteredStaffRevenue(filtered);
  }, [searchTerm, staffRevenue]);

  // Apply profit filters
  const applyProfitFilters = () => {
    fetchProfitData(selectedCategory, selectedMonthProfit, selectedYear);
  };

  // Reset all filters
  const resetProfitFilters = () => {
    setSelectedCategory("");
    setSelectedMonthProfit("");
    setSelectedYear("");
    setProfitSearchTerm("");
    fetchProfitData();
  };

  // Dropdown handlers
  const handleBookingDropdownToggle = () => setDropdownOpenBooking(!dropdownOpenBooking);
  const handleCakeDropdownToggle = () => setDropdownOpenCake(!dropdownOpenCake);
  const handleStaffDropdownToggle = () => setDropdownOpenStaff(!dropdownOpenStaff);

  const handleStaffFilterChange = async (filter) => {
    setStaffFilter(filter);
    await fetchStaffRevenue(filter);
  };

  // Handle booking filter change
  const handleBookingFilterChange = async (filter) => {
    setFilterBooking(filter);
    await fetchDashboardData(filter);
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredProfitData.length / itemsPerPage);
  const currentProfitData = filteredProfitData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Report cards data
  const reports = [
    { 
      title: "Theaters", 
      iconClass: "bx-building", 
      description: dashboardData.theatres, 
      bgColor: "bg-warning", 
      link: "/Theater",
      textColor: "text-white"
    },
    { 
      title: "Services", 
      iconClass: "bx-cube", 
      description: dashboardData.services, 
      bgColor: "gradient-info", 
      link: "/Services",
      textColor: "text-white"
    },
    { 
      title: "Bookings", 
      iconClass: "bx-receipt", 
      description: formatNumber(dashboardData.bookings), 
      bgColor: "gradient-success", 
      link: "/PendingBookings",
      textColor: "text-white"
    },
    { 
      title: "Revenue", 
      iconClass: "bx-rupee", 
      description: formatCurrency(dashboardData.amount), 
      bgColor: "bg-warning", 
      link: "/Payments",
      textColor: "text-white"
    },
    { 
      title: "Food Sales", 
      iconClass: "bx-restaurant", 
      description: formatCurrency(dashboardData.posAmount), 
      bgColor: "gradient-danger", 
      link: "/FoodProduct",
      textColor: "text-white"
    },
    { 
      title: "Staff", 
      iconClass: "bx-group", 
      description: dashboardData.staff, 
      bgColor: "gradient-success", 
      link: "/Staff",
      textColor: "text-white"
    },
    { 
      title: "Departments", 
      iconClass: "bx-briefcase", 
      description: dashboardData.departments, 
      bgColor: "bg-dark", 
      link: "/Departments",
      textColor: "text-white"
    },
    { 
      title: "Occasions", 
      iconClass: "bx-party", 
      description: dashboardData.occasions, 
      bgColor: "bg-light", 
      link: "/Occasions",
      textColor: "text-blue"
    },
  ];

  // Chart configurations - BACKEND RESPONSE KE HISAB SE
  const monthlyChartSeries = [
    { 
      name: "Bookings", 
      data: [currentFilterData.bookings || 0], 
      color: "#300843" 
    },
    { 
      name: "Revenue", 
      data: [currentFilterData.amount || 0], 
      color: "#f1b44c" 
    },
  ];

  const monthlyChartOptions = {
    chart: { 
      toolbar: { show: false },
      type: 'bar',
      height: 350
    },
    plotOptions: { 
      bar: { 
        horizontal: false, 
        columnWidth: "50%", 
        borderRadius: 4
      } 
    },
    dataLabels: { 
      enabled: true,
      formatter: function(val) {
        return val.toLocaleString('en-IN');
      }
    },
    stroke: { 
      show: true, 
      width: 2, 
      colors: ["transparent"] 
    },
    colors: ["#300843", "#f1b44c"],
    xaxis: { 
      categories: [filterBooking],
      labels: { 
        style: { 
          colors: '#6c757d', 
          fontSize: '14px',
          fontWeight: 'bold'
        } 
      }
    },
    yaxis: {
      labels: { 
        formatter: function(val) {
          return val.toLocaleString('en-IN');
        }
      }
    },
    grid: { 
      borderColor: "#f1f1f1",
      strokeDashArray: 4
    },
    fill: { opacity: 1 },
    tooltip: { 
      y: {
        formatter: function(val, { seriesIndex }) {
          if (seriesIndex === 0) {
            return val + ' bookings';
          } else {
            return '₹' + val.toLocaleString('en-IN');
          }
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right'
    }
  };

  // Cake Revenue Chart Configuration
  const cakeRevenueOptions = {
    chart: {
      type: 'bar',
      height: 350,
      background: '#fff',
      toolbar: { show: true }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded',
        borderRadius: 6
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: cakeRevenueData.months || [],
      labels: {
        style: {
          colors: '#6c757d',
          fontSize: '11px'
        },
        rotate: -45
      }
    },
    yaxis: {
      title: {
        text: 'Revenue (₹)',
        style: { 
          color: '#6c757d'
        }
      },
      labels: {
        formatter: function(val) {
          return formatCurrency(val);
        }
      }
    },
    title: {
      text: selectedCake || selectedCakeMonth 
        ? `Cake Revenue - ${selectedCake || 'All Cakes'} ${selectedCakeMonth ? `- ${selectedCakeMonth}` : ''}`
        : 'Cake Revenue - All Cakes & Months',
      align: 'center',
      style: { 
        color: '#495057', 
        fontSize: '16px', 
        fontWeight: 'bold'
      }
    },
    colors: ['#7367F0', '#28C76F', '#EA5455', '#FF9F43', '#1E90FF'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4
    },
    tooltip: {
      y: {
        formatter: function(val) {
          return formatCurrency(val);
        }
      }
    }
  };

  const cakeRevenueSeries = [
    {
      name: 'Revenue',
      data: cakeRevenueData.revenues || []
    }
  ];

  // Loading component
  if (loading.main) {
    return (
      <div className="page-content dashboard-container">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
            <div className="text-center">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 text-muted responsive-text">Loading Dashboard Data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content dashboard-container">
        <Container fluid>
          <Breadcrumbs 
            title="Carnival Castle Admin" 
            breadcrumbItem={props.t("Dashboard")} 
          />

          {/* Report Cards */}
          <Row className="mb-4">
            {reports.map((report, idx) => (
              <Col xl="3" md="6" key={idx}>
                <Link to={report.link} className="text-decoration-none">
                  <Card className={`report-card ${report.bgColor} ${report.textColor} mb-4 shadow-sm hover-lift card-hover`}>
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="mb-1 fw-medium responsive-text">{report.title}</p>
                          <h3 className="mb-0 stats-highlight">{report.description}</h3>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="icon-container">
                            <i className={`bx ${report.iconClass} font-size-24 ${report.textColor}`} />
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>

          {/* Bookings & Revenue Chart - BACKEND RESPONSE DIRECT SHOW */}
          <Row className="mb-4">
            <Col lg="12">
              <Card className="shadow-sm border-0 card-hover">
                <CardBody className="chart-container">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0 fw-bold text-dark">Bookings & Revenue Analytics ({filterBooking})</h5>
                    <Dropdown isOpen={dropdownOpenBooking} toggle={handleBookingDropdownToggle} className="filter-dropdown">
                      <DropdownToggle caret color="outline-primary" size="sm" className="btn-custom">
                        <i className="bx bx-filter-alt me-1"></i>
                        {filterBooking}
                      </DropdownToggle>
                      <DropdownMenu>
                        {["Today", "This Week", "Last Week", "This Month", "Last Month"].map(item => (
                          <DropdownItem 
                            key={item} 
                            onClick={() => handleBookingFilterChange(item)} 
                            className="responsive-text"
                          >
                            {item}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  
                  {/* Stats Summary - BACKEND DATA DIRECT */}
                  <Row className="mb-3">
                    <Col md="6">
                      <Card className="border-0 bg-light">
                        <CardBody className="py-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bx bx-receipt text-primary fs-4 me-2"></i>
                              <span className="fw-medium responsive-text">Bookings:</span>
                            </div>
                            <span className="fw-bold text-primary fs-5">
                              {formatNumber(currentFilterData.bookings || 0)}
                            </span>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="6">
                      <Card className="border-0 bg-light">
                        <CardBody className="py-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <i className="bx bx-rupee text-success fs-4 me-2"></i>
                              <span className="fw-medium responsive-text">Revenue:</span>
                            </div>
                            <span className="fw-bold text-success fs-5">
                              {formatCurrency(currentFilterData.amount || 0)}
                            </span>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  
                  {/* Simple Bar Chart - BACKEND DATA */}
                  <div style={{ height: '300px' }}>
                    <ReactApexChart 
                      options={monthlyChartOptions} 
                      series={monthlyChartSeries} 
                      type="bar" 
                      height="100%" 
                    />
                  </div>
                  
                  {/* Details Table - BACKEND DATA */}
                  {currentFilterData.details && currentFilterData.details.length > 0 && (
                    <div className="mt-4">
                      <h6 className="mb-3 fw-bold">Booking Details ({filterBooking})</h6>
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead>
                            <tr className="bg-light">
                              <th>#</th>
                              <th>Order ID</th>
                              <th>Date</th>
                              <th>Theater</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentFilterData.details.map((detail, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>
                                  <Badge color="primary" className="badge-soft-primary">
                                    {detail.orderId}
                                  </Badge>
                                </td>
                                <td>{detail.date}</td>
                                <td>{detail.theatreName}</td>
                                <td className="text-end fw-bold text-success">
                                  {formatCurrency(detail.totalPrice)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                  
                  {currentFilterData.details && currentFilterData.details.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <i className="bx bx-data display-4 d-block mb-2"></i>
                      <p>No booking details available for {filterBooking}</p>
                    </div>
                  )}
                  
                  <div className="mt-3 text-center text-muted small">
                    <i className="bx bx-info-circle me-1"></i>
                    Showing data for: <strong>{filterBooking}</strong> | 
                    Total Bookings: <strong>{formatNumber(currentFilterData.bookings || 0)}</strong> | 
                    Total Revenue: <strong>{formatCurrency(currentFilterData.amount || 0)}</strong>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Revenue by Category Section */}
          <Row className="mb-4">
            <Col lg="12">
              <Card className="shadow-sm border-0 card-hover">
                <CardBody>
                  <h5 className="card-title mb-4 fw-bold text-dark">Revenue by Category</h5>
                  
                  {/* Filters Row */}
                  <Row className="mb-3 g-2">
                    <Col md={3}>
                      <Input
                        type="text"
                        placeholder="Search by name..."
                        value={profitSearchTerm}
                        onChange={(e) => setProfitSearchTerm(e.target.value)}
                        className="search-input border-0 shadow-sm"
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="search-input border-0 shadow-sm"
                      >
                        <option value="">All Categories</option>
                        <option value="cakes">Cakes</option>
                        <option value="decorations">Decorations</option>
                        <option value="roses">Roses</option>
                        <option value="photography">Photography</option>
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={selectedMonthProfit}
                        onChange={(e) => setSelectedMonthProfit(e.target.value)}
                        className="search-input border-0 shadow-sm"
                      >
                        <option value="">All Months</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i} value={String(i + 1).padStart(2, "0")}>
                            {new Date(0, i).toLocaleString("default", { month: "long" })}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Input
                        type="select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className="search-input border-0 shadow-sm"
                      >
                        <option value="">All Years</option>
                        {[2024, 2025, 2026].map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={1}>
                      <Button color="primary" onClick={applyProfitFilters} className="w-100 shadow-sm btn-custom">
                        <i className="bx bx-check me-1"></i>
                        Apply
                      </Button>
                    </Col>
                    <Col md={1}>
                      <Button color="outline-secondary" onClick={resetProfitFilters} className="w-100 shadow-sm btn-custom">
                        <i className="bx bx-reset me-1"></i>
                        Reset
                      </Button>
                    </Col>
                  </Row>

                  {/* Table */}
                  <div className="table-responsive table-responsive-custom">
                    <Table hover className="mb-0 table-hover-custom">
                      <thead className="table-header-custom">
                        <tr>
                          <th className="text-blue">Name</th>
                          <th className="text-blue">Category</th>
                          <th className="text-blue">Price</th>
                          <th className="text-blue">Total Quantity</th>
                          <th className="text-blue">Total Revenue</th>
                          <th className="text-blue">Booking Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading.profit ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <Spinner size="sm" className="me-2" />
                              <span className="responsive-text">Loading revenue data...</span>
                            </td>
                          </tr>
                        ) : currentProfitData.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              <i className="bx bx-data display-4 d-block mb-2"></i>
                              <span className="responsive-text">No revenue data found</span>
                            </td>
                          </tr>
                        ) : (
                          currentProfitData.map((item, index) => (
                            <tr key={item._id || index}>
                              <td className="fw-medium responsive-text">{item.name}</td>
                              <td>
                                <Badge color="info" className="badge-soft-info status-badge">
                                  {item.categoryName}
                                </Badge>
                              </td>
                              <td className="responsive-text">{formatCurrency(item.price)}</td>
                              <td className="responsive-text">{formatNumber(item.totalBookingQuantity)}</td>
                              <td className="fw-bold text-success responsive-text">{formatCurrency(item.totalRevenue)}</td>
                              <td>
                                <Badge color="primary" className="badge-soft-primary status-badge">
                                  {item.bookingCount}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {filteredProfitData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted responsive-text">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProfitData.length)} of {filteredProfitData.length} entries
                      </div>
                      <div className="d-flex gap-2 pagination-custom">
                        <Button
                          color="outline-primary"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={() => goToPage(currentPage - 1)}
                          className="btn-custom"
                        >
                          <i className="bx bx-chevron-left"></i> Previous
                        </Button>
                        <div className="d-flex align-items-center mx-2 responsive-text">
                          Page {currentPage} of {totalPages}
                        </div>
                        <Button
                          color="outline-primary"
                          size="sm"
                          disabled={currentPage === totalPages}
                          onClick={() => goToPage(currentPage + 1)}
                          className="btn-custom"
                        >
                          Next <i className="bx bx-chevron-right"></i>
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Cake Revenue Chart */}
          <Row className="mb-4">
            <Col lg="12">
              <Card className="shadow-sm border-0 card-hover cake-chart-card">
                <CardBody className="chart-container">
                  <div className="d-flex justify-content-between align-items-center mb-4 cake-chart-header">
                    <h5 className="card-title mb-0 fw-bold text-dark">
                      Cake Revenue Analytics
                    </h5>
                  </div>

                  {/* Cake Filters Section */}
                  <div className="cake-filter-section">
                    <Row className="g-3">
                      <Col md={4}>
                        <label className="form-label fw-medium">Filter by Cake</label>
                        <Input
                          type="select"
                          value={selectedCake}
                          onChange={(e) => setSelectedCake(e.target.value)}
                          className="search-input border-0 shadow-sm"
                        >
                          <option value="">All Cakes</option>
                          {getUniqueCakes().map((cake, index) => (
                            <option key={index} value={cake}>
                              {cake}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={4}>
                        <label className="form-label fw-medium">Filter by Month</label>
                        <Input
                          type="select"
                          value={selectedCakeMonth}
                          onChange={(e) => setSelectedCakeMonth(e.target.value)}
                          className="search-input border-0 shadow-sm"
                        >
                          <option value="">All Months</option>
                          {getUniqueMonths().map((month, index) => (
                            <option key={index} value={month}>
                              {month}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <label className="form-label">&nbsp;</label>
                        <Button 
                          color="primary" 
                          onClick={applyCakeFilters} 
                          className="w-100 shadow-sm btn-custom"
                        >
                          <i className="bx bx-filter-alt me-1"></i>
                          Apply
                        </Button>
                      </Col>
                      <Col md={2}>
                        <label className="form-label">&nbsp;</label>
                        <Button 
                          color="outline-secondary" 
                          onClick={resetCakeFilters} 
                          className="w-100 shadow-sm btn-custom"
                        >
                          <i className="bx bx-reset me-1"></i>
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {loading.cakes ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                      <Spinner color="primary" />
                      <span className="ms-2 responsive-text">Loading cake data...</span>
                    </div>
                  ) : (
                    <ReactApexChart
                      options={cakeRevenueOptions}
                      series={cakeRevenueSeries}
                      type="bar"
                      height={350}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Latest Bookings & Staff Revenue in one row */}
          <Row className="mb-4">
            {/* Latest Bookings */}
            <Col lg="6">
              <Card className="shadow-sm border-0 card-hover h-100">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0 fw-bold text-dark">Latest Bookings</h5>
                    <div className="d-flex align-items-center gap-2">
                      <Badge color="primary" pill className="status-badge">
                        {filteredLatestBookings?.length || 0}
                      </Badge>
                      <Input
                        type="select"
                        value={selectedBookingSource}
                        onChange={(e) => setSelectedBookingSource(e.target.value)}
                        size="sm"
                        className="search-input border-0 shadow-sm"
                        style={{ minWidth: '150px' }}
                      >
                        <option value="">All Sources</option>
                        {getUniqueBookingSources().map((source, index) => (
                          <option key={index} value={source}>
                            {source}
                          </option>
                        ))}
                      </Input>
                      {selectedBookingSource && (
                        <Button 
                          color="outline-secondary" 
                          onClick={resetLatestBookingsFilter} 
                          size="sm"
                          className="btn-custom"
                        >
                          <i className="bx bx-reset"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="table-responsive table-responsive-custom">
                    <Table hover className="mb-0 table-hover-custom">
                      <thead className="table-header-custom">
                        <tr>
                          <th className="text-blue">#</th>
                          <th className="text-blue">Booking Details</th>
                          <th className="text-blue">Customer</th>
                          <th className="text-blue">Status</th>
                          <th className="text-blue">Theater</th>
                          <th className="text-blue">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLatestBookings?.slice(0, 5).map((booking, index) => (
                          <tr key={booking._id}>
                            <td className="fw-medium responsive-text">{index + 1}</td>
                            <td>
                              <div>
                                <div className="fw-medium text-primary responsive-text">{booking.orderId}</div>
                                <small className="text-muted responsive-text">
                                  {booking.date} • {booking.time}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="fw-medium responsive-text">{booking.userName}</div>
                                <small className="text-muted responsive-text">{booking.userPhone}</small>
                              </div>
                            </td>
                            <td>
                              <Badge 
                                color={
                                  booking.status === 'booking-confirmed' ? 'success' : 
                                  booking.status === 'pending' ? 'warning' : 'secondary'
                                }
                                className="badge-soft status-badge"
                              >
                                {booking.status}
                              </Badge>
                            </td>
                             <td>
                              <div>
                                <div className="fw-medium responsive-text">{booking.theatreName}</div>
                              </div>
                            </td>
                             <td>
                              <Badge color="info" className="badge-soft-info status-badge">
                                {booking.bookingSource}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                        {filteredLatestBookings?.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              <span className="responsive-text">
                                {selectedBookingSource 
                                  ? `No bookings found for source: ${selectedBookingSource}`
                                  : "No latest bookings available"
                                }
                              </span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  {filteredLatestBookings?.length > 5 && (
                    <div className="text-center mt-3">
                      <Link to="/PendingBookings" className="btn btn-outline-primary btn-sm btn-custom">
                        View All Bookings
                      </Link>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Staff Revenue */}
            <Col lg="6">
              <Card className="shadow-sm border-0 card-hover h-100">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0 fw-bold text-dark">Staff Performance</h5>
                    <Dropdown isOpen={dropdownOpenStaff} toggle={handleStaffDropdownToggle} className="filter-dropdown">
                      <DropdownToggle caret color="outline-primary" size="sm" className="btn-custom">
                        <i className="bx bx-filter-alt me-1"></i>
                        {staffFilter}
                      </DropdownToggle>
                      <DropdownMenu>
                        {["Today", "This Week", "Last Week", "This Month", "Last Month", "This Year", "Last Year"].map(item => (
                          <DropdownItem key={item} onClick={() => handleStaffFilterChange(item)} className="responsive-text">
                            {item}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  
                  <InputGroup className="mb-3">
                    <InputGroupText className="search-input">
                      <i className="bx bx-search"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search staff..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input border-0 shadow-sm"
                    />
                  </InputGroup>

                  <div className="table-responsive table-responsive-custom">
                    <Table hover className="mb-0 table-hover-custom">
                      <thead className="table-header-custom">
                        <tr>
                          <th className="text-blue">Staff</th>
                          <th className="text-blue">Revenue</th>
                          <th className="text-blue">Bookings</th>
                          <th className="text-blue">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading.staff ? (
                          <tr>
                            <td colSpan="4" className="text-center py-3">
                              <Spinner size="sm" className="me-2" />
                              <span className="responsive-text">Loading staff data...</span>
                            </td>
                          </tr>
                        ) : filteredStaffRevenue.length > 0 ? (
                          filteredStaffRevenue.slice(0, 5).map((staff, index) => (
                            <tr key={staff.staffId}>
                              <td>
                                <div>
                                  <div className="fw-medium responsive-text">{staff.staffName}</div>
                                  <small className="text-muted responsive-text">{staff.staffEmail}</small>
                                </div>
                              </td>
                              <td className="fw-bold text-success responsive-text">
                                {formatCurrency(staff.totalRevenue)}
                              </td>
                              <td>
                                <Badge color="primary" className="badge-soft-primary status-badge">
                                  {staff.totalBookings}
                                </Badge>
                              </td>
                              <td>
                                <Badge color="success" className="badge-soft-success status-badge">
                                  {staff.completedBookings}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-3 text-muted">
                              <span className="responsive-text">No staff data found</span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Most Booked Theaters */}
         <Row className="mb-4">
  <Col lg="12">
    <Card className="shadow-sm border-0 card-hover h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0 fw-bold text-dark">Most Booked Theaters</h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="d-flex gap-2">
              <Input
                type="select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                size="sm"
                className="search-input border-0 shadow-sm"
                style={{ minWidth: '140px' }}
              >
                <option value="">All Months</option>
                {getUniqueTheaterMonths().map((month, index) => (
                  <option key={index} value={month}>
                    {month}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                value={selectedAddress}
                onChange={(e) => setSelectedAddress(e.target.value)}
                size="sm"
                className="search-input border-0 shadow-sm"
                style={{ minWidth: '160px' }}
              >
                <option value="">All Addresses</option>
                {getUniqueAddresses().map((address, index) => (
                  <option key={index} value={address}>
                    {address}
                  </option>
                ))}
              </Input>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={resetTheaterFilters} 
              size="sm"
              className="btn-custom"
            >
              <i className="bx bx-reset me-1"></i>
              Reset
            </Button>
          </div>
        </div>
        
        {loading.theaters ? (
          <div className="text-center py-4">
            <Spinner size="sm" className="me-2" />
            <span className="responsive-text">Loading theater data...</span>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 table-hover-custom">
              <thead className="table-header-custom">
                <tr>
                  <th className="text-blue">#</th>
                  <th className="text-blue">Theater</th>
                  <th className="text-blue">Address</th>
                  <th className="text-blue">Month</th>
                  <th className="text-blue">Bookings</th>
                  <th className="text-blue">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {filteredTheaters.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      <span className="responsive-text">No theater data found</span>
                    </td>
                  </tr>
                ) : (
                  filteredTheaters
                    .slice(0, 5)
                    .map((theater, index) => {
                      // RESPONSE KE HISAB SE DATA ACCESS KARO
                      const theaterName = theater.theatreName || "N/A";
                      const addressName = theater.addressName || "N/A";
                      const monthName = theater.monthName || "N/A";
                      const totalBookings = theater.totalBookings || 0;
                      const totalRevenue = theater.totalRevenue || 0;
                      
                      return (
                        <tr key={theater.theatreId || index}>
                          <td className="fw-medium responsive-text">{index + 1}</td>
                          <td className="fw-medium responsive-text">{theaterName}</td>
                          <td>
                            <div className="fw-medium responsive-text">{addressName}</div>
                          </td>
                          <td className="responsive-text">{monthName}</td>
                          <td>
                            <Badge color="primary" className="badge-soft-primary status-badge">
                              {totalBookings}
                            </Badge>
                          </td>
                          <td className="fw-bold text-success responsive-text">
                            {formatCurrency(totalRevenue)}
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  </Col>
</Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);